import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Scan, HeartPulse, Sparkles, Crown, Menu, X, LogOut, Users, User, Layers, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Disclaimer } from "@/components/Disclaimer";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const LangToggle = () => {
  const { lang, setLanguage } = useLang();
  return (
    <div className="flex items-center rounded-full border border-gray-200 bg-white p-0.5 text-xs font-semibold" data-testid="nav-language-toggle">
      {["es", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLanguage(l)}
          data-testid={`nav-language-${l}`}
          className={`rounded-full px-3 py-1 transition-colors ${lang === l ? "bg-emerald-500 text-white" : "text-gray-500 hover:text-gray-800"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const GUEST_NAV = [
  { to: "/evidence", key: "nav.evidence", icon: HeartPulse, tid: "nav-evidence-link" },
  { to: "/pricing", key: "nav.pricing", icon: Crown, tid: "nav-pricing-link" },
];

const AUTH_NAV = [
  { to: "/scan", key: "nav.scan", icon: Scan, tid: "nav-scan-link" },
  { to: "/evidence", key: "nav.evidence", icon: HeartPulse, tid: "nav-evidence-link" },
  { to: "/stack", key: "nav.myStack", icon: Layers, tid: "nav-stack-link" },
  { to: "/formula", key: "nav.formula", icon: Sparkles, tid: "nav-formula-link" },
  { to: "/community", key: "nav.community", icon: Users, tid: "nav-community-link" },
  { to: "/pricing", key: "nav.pricing", icon: Crown, tid: "nav-pricing-link" },
];

export const Layout = ({ children }) => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const navItems = user ? AUTH_NAV : GUEST_NAV;

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-gray-900">
      <header className="sticky top-0 z-40 border-b border-gray-200/70 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center">
            <Logo size={44} />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold transition-colors ${
                    isActive ? "bg-emerald-50 text-emerald-700" : "text-gray-600 hover:bg-gray-100"
                  }`
                }
              >
                <n.icon size={16} />
                {t(n.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LangToggle />
            {user ? (
              <div className="hidden items-center gap-2 lg:flex">
                {user.is_pro && (
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">
                    {t("common.premium")}
                  </span>
                )}
                <Link to="/profile" data-testid="nav-profile-link" className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100">
                  <User size={15} className="text-emerald-600" />
                  <span className="max-w-[110px] truncate" data-testid="nav-user-menu">{user.name}</span>
                </Link>
                <button onClick={handleLogout} data-testid="nav-logout-button" className="rounded-full p-2 text-gray-500 hover:bg-gray-100">
                  <LogOut size={17} />
                </button>
              </div>
            ) : (
              <div className="hidden items-center gap-2 lg:flex">
                <Link to="/login" data-testid="nav-login-button" className="rounded-full px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
                  {t("nav.login")}
                </Link>
                <Link to="/register" data-testid="nav-register-button" className="rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600">
                  {t("nav.register")}
                </Link>
              </div>
            )}
            <button className="rounded-full p-2 text-gray-600 lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t border-gray-100 bg-white px-4 py-3 lg:hidden">
            <div className="flex flex-col gap-1">
              {navItems.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${isActive ? "bg-emerald-50 text-emerald-700" : "text-gray-700"}`}>
                  <n.icon size={17} /> {t(n.key)}
                </NavLink>
              ))}
              <div className="mt-2 border-t border-gray-100 pt-2">
                {user ? (
                  <>
                    <NavLink to="/profile" onClick={() => setOpen(false)} data-testid="nav-profile-link-mobile"
                      className={({ isActive }) => `flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${isActive ? "bg-emerald-50 text-emerald-700" : "text-gray-700"}`}>
                      <User size={17} /> {t("nav.profile")}
                    </NavLink>
                    <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-700">
                      <LogOut size={17} /> {t("nav.logout")}
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Link to="/login" onClick={() => setOpen(false)} className="rounded-full border border-gray-200 px-4 py-2 text-center text-sm font-semibold">{t("nav.login")}</Link>
                    <Link to="/register" onClick={() => setOpen(false)} className="rounded-full bg-emerald-500 px-4 py-2 text-center text-sm font-semibold text-white">{t("nav.register")}</Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">{children}</main>

      <footer className="mt-8 border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="mb-5 flex items-center gap-3">
            <Logo size={38} />
            <p className="text-xs text-gray-500">{t("brand.tagline")}</p>
          </div>
          <Disclaimer compact />
          <div className="mt-4 flex flex-col items-start justify-between gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center">
            <p className="flex items-center gap-1.5 text-xs text-gray-400">
              <Globe size={13} /> © {new Date().getFullYear()} Suppev · PubMed / NCBI · WADA
            </p>
            <div className="flex items-center gap-5 text-xs font-semibold text-gray-500">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-emerald-600">{t("footer.privacy")}</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-emerald-600">{t("footer.terms")}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
